import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { useInventarioData } from '@/hooks/useInventarioData'
import { useInventarioFilters } from '@/hooks/useInventarioFilters'
import { InventarioKpis } from '@/components/inventario/InventarioKpis'
import { PAGE_KPIS } from '@/config/uiFlags'
import { InventarioFilters } from '@/components/inventario/InventarioFilters'
import { InventarioTable } from '@/components/inventario/InventarioTable'
import { InventarioDonut } from '@/components/inventario/InventarioDonut'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { catalogoArticulos } from '@/utils/filtroArticulo'
import { PageHeader }    from '@/components/common/PageHeader'
import { RefreshButton } from '@/components/common/RefreshButton'

function useIsArmafLocked(): boolean {
  const empresas = useAllowedEmpresas()
  if (!empresas.length) return false
  return empresas.includes('avp') && empresas.length === 1
}

export default function InventarioPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const isArmafLocked = useIsArmafLocked()
  const fmtArt = useFmtArticulo()
  const [showChart, setShowChart] = useState(false)

  const {
    rows, isLoading, isError, refetch,
    kpis, allEmpresas, isEmpresaAll, allAlmacenes, allLineas, allFamilias, updatedAt,
  } = useInventarioData()

  const {
    articulo, descripcion, terminos, empresa, almacen, linea, familia, tipo, soloStock, sortKey, sortDir,
    setArticulo, setDescripcion, setTerminos, setEmpresa, setAlmacen, setLinea, setFamilia, setTipo, setSoloStock, setSortKey, setSortDir, clearAll,
    filtered, sorted, paged,
    page, setPage, totalPages, pageSize, activeFilters,
  } = useInventarioFilters(rows)

  // Catálogo de artículos de lo que hay cargado, para sugerir al escribir. No
  // limita la búsqueda: el campo sigue aceptando texto libre, porque también
  // busca por línea, familia y almacén.
  // Se enseña el código formateado y se filtra por el crudo: la tabla ya pinta
  // el formateado, y con el crudo en el desplegable el mismo artículo aparecía
  // con dos nombres distintos en la misma pantalla.
  const opcionesArticulo = useMemo(
    () => catalogoArticulos(rows, r => r.articulo, r => r.descripcion, fmtArt),
    [rows, fmtArt],
  )

  const pageBg   = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const btnBase  = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>

      <PageHeader
        dark={dark}
        title={t('inventory.title')}
        subtitle={
          <>
            {t('inventory.subtitle')} · {filtered.length} {t('common.of')} {rows.length} {t('common.records')}
            {activeFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                · {t('common.activeFilter', { count: activeFilters })}
              </span>
            )}
            {updatedAt && (
              <span className="ml-2">
                · {t('common.refresh')} {new Date(updatedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </>
        }
        actions={
          <>
            {isError && (
              <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>
            )}
            <RefreshButton dark={dark} onClick={() => refetch()} />
            <button
              onClick={() => setShowChart(!showChart)}
              className={showChart
                ? `${btnBase} bg-sky-500 border-sky-500 text-white`
                : btnNeutral
              }
            >
              <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="9" width="3" height="5" rx="0.5"/>
                <rect x="6.5" y="5" width="3" height="9" rx="0.5"/>
                <rect x="11" y="2" width="3" height="12" rx="0.5"/>
              </svg>
              {t('production.chart.modeTotal')}
            </button>
          </>
        }
      />

      {PAGE_KPIS.inventory && (
        <InventarioKpis
          dark={dark}
          isLoading={isLoading}
          skusConStock={kpis.skusConStock}
          skusEnCero={kpis.skusEnCero}
          totalSkus={kpis.totalSkus}
          totalUnidades={kpis.totalUnidades}
          filteredCount={filtered.length}
        />
      )}

      {showChart && !isLoading && filtered.length > 0 && (
        <InventarioDonut dark={dark} rows={filtered} />
      )}

      <InventarioFilters
        dark={dark}
        articulo={articulo} descripcion={descripcion} terminos={terminos}
        opcionesArticulo={opcionesArticulo}
        empresa={empresa}
        almacen={almacen}
        linea={linea}
        familia={familia}
        tipo={tipo}
        soloStock={soloStock}
        sortKey={sortKey}
        sortDir={sortDir}
        resumen={`${filtered.length} ${t('common.of')} ${rows.length} ${t('common.records')}`}
        allEmpresas={allEmpresas}
        allAlmacenes={allAlmacenes}
        allLineas={allLineas}
        allFamilias={allFamilias}
        isArmafLocked={isArmafLocked}
        isEmpresaAll={isEmpresaAll}
        setArticulo={setArticulo} setDescripcion={setDescripcion} setTerminos={setTerminos}
        setEmpresa={setEmpresa}
        setAlmacen={setAlmacen}
        setLinea={setLinea}
        setFamilia={setFamilia}
        setTipo={setTipo}
        setSoloStock={setSoloStock}
        setSortKey={setSortKey}
        setSortDir={setSortDir}
        clearAll={clearAll}
      />

      <InventarioTable
        dark={dark}
        paged={paged}
        sorted={sorted}
        sortKey={sortKey}
        sortDir={sortDir}
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        isLoading={isLoading}
        isEmpresaAll={isEmpresaAll}
        isAvpOnly={isArmafLocked}
        setSortKey={setSortKey}
        setSortDir={setSortDir}
        setPage={setPage}
      />

    </div>
  )
}
